import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

const HANDLE_RADIUS = 5;
const ROTATION_HANDLE_RADIUS = 6;
const ROTATION_HANDLE_HIT_RADIUS = 20;
const ROTATION_HANDLE_OFFSET = 20;
const ROTATION_BRIDGE_WIDTH = 24;
const DELETE_BUTTON_RADIUS = 10;
const DELETE_BUTTON_MARGIN = 12;

const CLASSIFICATION_HEIGHT = 22;
const CLASSIFICATION_MENU_HEIGHT = 220;
const MEASUREMENT_OFFSET = CLASSIFICATION_HEIGHT + 5;

const MAX_DIMENSION_CM = 2.5;
const MAX_AREA_CM2 = 4;

const DEFECT_CLASSES = [
  'cp joint',
  'fod',
  'over-sanding',
  'porosity',
  'delamination',
  'glue lacking',
  'resin lacking',
  'glass fold',
  'glass color',
  'glass excess',
  'glass tearing',
  'drilling',
  'imprint',
  'stringer',
  'skin',
  'stripe',
  'impact',
  'other',
];

const normalizeAngle = (angle) => {
  return ((angle % 180) + 180) % 180;
};

const getResizeCursor = (handle, angle) => {
  const baseAngle = ['e', 'w'].includes(handle) ? 0 : 90;
  const effectiveAngle = normalizeAngle(baseAngle - angle);

  if (effectiveAngle < 22.5 || effectiveAngle >= 157.5) return 'ew-resize';
  if (effectiveAngle < 67.5) return 'nwse-resize';
  if (effectiveAngle < 112.5) return 'ns-resize';

  return 'nesw-resize';
};

const formatMeasurement = (value) => {
  return Number(value).toFixed(2).replace(/\.?0+$/, '');
};

const formatDefectClass = (defectClass) => {
  return defectClass.charAt(0).toUpperCase() + defectClass.slice(1);
};

const getClassificationWidth = () => {
  const longestLabel = DEFECT_CLASSES.reduce((longest, defectClass) => {
    const label = formatDefectClass(defectClass);
    return label.length > longest.length ? label : longest;
  }, '');

  return longestLabel.length * 7 + 34;
};

const CLASSIFICATION_WIDTH = getClassificationWidth();

const isCriticalAnnotation = (annotation) => {
  return Number(annotation.width_cm ?? 0) > MAX_DIMENSION_CM ||
    Number(annotation.height_cm ?? 0) > MAX_DIMENSION_CM ||
    Number(annotation.area_cm2 ?? 0) > MAX_AREA_CM2;
};

export const AnnotationObb = ({
                                annotation,
                                imageWidth,
                                imageHeight,
                                selected,
                                hovered,
                                disabled,
                                onMouseEnter,
                                onMouseLeave,
                                onMoveStart,
                                onResizeStart,
                                onRotateStart,
                                onClassChange,
                                onDelete,
                              }) => {
  const dropdownRef = useRef(null);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);

  const centerX = annotation.x * imageWidth;
  const centerY = annotation.y * imageHeight;
  const width = annotation.width * imageWidth;
  const height = annotation.height * imageHeight;
  const angle = annotation.angle ?? 0;
  const defectClass = annotation.class ?? 'other';

  const left = centerX - width / 2;
  const right = centerX + width / 2;
  const top = centerY - height / 2;
  const bottom = centerY + height / 2;

  const handles = [
    { type: 'n', x: centerX, y: top },
    { type: 'e', x: right, y: centerY },
    { type: 's', x: centerX, y: bottom },
    { type: 'w', x: left, y: centerY },
  ];

  const deleteX = right - DELETE_BUTTON_MARGIN;
  const deleteY = top + DELETE_BUTTON_MARGIN;
  const rotationX = centerX;
  const rotationY = top - ROTATION_HANDLE_OFFSET;

  const showControls = (selected || hovered) && !disabled;
  const showDeleteButton = hovered && !disabled;
  const hasMeasurements = annotation.width_cm !== undefined && annotation.height_cm !== undefined && annotation.area_cm2 !== undefined;
  const isCritical = isCriticalAnnotation(annotation);

  const measurementText = hasMeasurements
    ? `${formatMeasurement(annotation.width_cm)} × ${formatMeasurement(annotation.height_cm)} cm · ${formatMeasurement(annotation.area_cm2)} cm²`
    : null;

  const boxClass = isCritical
    ? 'fill-orange-500/20 stroke-orange-500'
    : 'fill-perception-blue/15 stroke-perception-blue';

  const controlClass = isCritical
    ? 'fill-white stroke-orange-500'
    : 'fill-white stroke-perception-blue';

  const controlStrokeClass = isCritical
    ? 'stroke-orange-500'
    : 'stroke-perception-blue';

  const labelColor = isCritical ? '#f97316' : 'white';

  useEffect(() => {
    const handleDropdownOpen = (event) => {
      if (event.detail !== annotation.id) setIsClassDropdownOpen(false);
    };

    window.addEventListener('annotation-class-dropdown-open', handleDropdownOpen);

    return () => window.removeEventListener('annotation-class-dropdown-open', handleDropdownOpen);
  }, [annotation.id]);

  useEffect(() => {
    if (!isClassDropdownOpen) return;

    const handlePointerDown = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsClassDropdownOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isClassDropdownOpen]);

  useEffect(() => {
    if (disabled) setIsClassDropdownOpen(false);
  }, [disabled]);

  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return;

    const handleWheel = (event) => {
      event.stopPropagation();
    };

    dropdown.addEventListener('wheel', handleWheel, { passive: true });

    return () => dropdown.removeEventListener('wheel', handleWheel);
  }, []);

  const handleToggleDropdown = () => {
    if (disabled) return;

    if (!isClassDropdownOpen) {
      window.dispatchEvent(new CustomEvent('annotation-class-dropdown-open', {
        detail: annotation.id,
      }));
    }

    setIsClassDropdownOpen((currentValue) => !currentValue);
  };

  const handleSelectClass = (defect) => {
    onClassChange(defect);
    setIsClassDropdownOpen(false);
  };

  return (
    <g transform={`rotate(${-angle} ${centerX} ${centerY})`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <rect x={left} y={top} width={width} height={height}
            className={`${disabled ? 'cursor-not-allowed' : 'cursor-move'} ${boxClass}`}
            strokeWidth={selected ? 3 : 2} onPointerDown={onMoveStart} />

      {measurementText && (
        <text x={left} y={top - MEASUREMENT_OFFSET} fill={labelColor} fontSize="13" fontWeight="600"
              style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: 'rgba(0, 0, 0, 0.75)', strokeWidth: 3, strokeLinejoin: 'round' }}>
          {measurementText}
        </text>
      )}

      <foreignObject x={left} y={top - CLASSIFICATION_HEIGHT} width={CLASSIFICATION_WIDTH}
                     height={isClassDropdownOpen ? CLASSIFICATION_HEIGHT + CLASSIFICATION_MENU_HEIGHT : CLASSIFICATION_HEIGHT}
                     style={{ overflow: 'visible' }}>
        <div ref={dropdownRef} xmlns="http://www.w3.org/1999/xhtml" className="relative w-full text-xs">
          <button type="button" disabled={disabled}
                  className={`h-[22px] w-full flex items-center justify-between gap-2 rounded bg-black/60 px-2 font-semibold ${
                    disabled ? 'cursor-default' : 'cursor-pointer hover:bg-black/75'
                  } ${isCritical ? 'text-orange-500' : 'text-white'}`}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleDropdown();
                  }}>
            <span className="whitespace-nowrap">{formatDefectClass(defectClass)}</span>

            {!disabled && (
              <ChevronDownIcon className={`size-3 shrink-0 transition-transform ${isClassDropdownOpen ? 'rotate-180' : ''}`} />
            )}
          </button>

          {isClassDropdownOpen && !disabled && (
            <div className="absolute z-50 top-[24px] left-0 w-full max-h-[220px] overflow-y-auto rounded-md border border-perception-gray-700 bg-perception-black-800 shadow-lg">
              {DEFECT_CLASSES.map((defect) => {
                const isSelected = defect === defectClass;

                return (
                  <button key={defect} type="button"
                          className={`w-full px-2 py-1.5 text-left whitespace-nowrap transition-colors ${
                            isSelected
                              ? 'bg-perception-blue/20 text-perception-blue font-semibold'
                              : 'text-perception-gray-300 hover:bg-perception-black-600 hover:text-white'
                          }`}
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectClass(defect);
                          }}>
                    {formatDefectClass(defect)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </foreignObject>

      {showControls && handles.map((handle) => (
        <circle key={handle.type} cx={handle.x} cy={handle.y} r={HANDLE_RADIUS} className={controlClass}
                strokeWidth="2" style={{ cursor: getResizeCursor(handle.type, angle) }}
                onPointerDown={(event) => onResizeStart(event, handle.type)} />
      ))}

      {showControls && (
        <>
          <line x1={centerX} y1={top} x2={rotationX} y2={rotationY} stroke="transparent" strokeWidth={ROTATION_BRIDGE_WIDTH}
                style={{ cursor: 'grab' }} onPointerDown={onRotateStart} />

          <circle cx={rotationX} cy={rotationY} r={ROTATION_HANDLE_HIT_RADIUS} fill="transparent"
                  style={{ cursor: 'grab' }} onPointerDown={onRotateStart} />

          <line x1={centerX} y1={top} x2={rotationX} y2={rotationY} className={controlStrokeClass} strokeWidth="2"
                style={{ pointerEvents: 'none' }} />

          <circle cx={rotationX} cy={rotationY} r={ROTATION_HANDLE_RADIUS} className={controlClass}
                  strokeWidth="2" style={{ pointerEvents: 'none' }} />
        </>
      )}

      {showDeleteButton && (
        <g className="cursor-pointer" onPointerDown={(event) => event.stopPropagation()} onClick={onDelete}>
          <circle cx={deleteX} cy={deleteY} r={DELETE_BUTTON_RADIUS} className="fill-perception-error-500" />

          <line x1={deleteX - 4} y1={deleteY - 4} x2={deleteX + 4} y2={deleteY + 4}
                stroke="white" strokeWidth="2" strokeLinecap="round" />

          <line x1={deleteX + 4} y1={deleteY - 4} x2={deleteX - 4} y2={deleteY + 4}
                stroke="white" strokeWidth="2" strokeLinecap="round" />
        </g>
      )}
    </g>
  );
};